import type { Source, StatusCounts, Target } from '../types';
import { SOURCE_LABELS, STATUSES } from '../types';
import { SOURCE_ICON } from '../assets';
import { TargetTable } from './TargetTable';

interface Props {
  source: Source;
  orgId?: string | null;
  targets: Target[];
  counts: StatusCounts;
  /** True when a status filter or search is narrowing the rows. */
  filtered: boolean;
  canHide?: boolean;
  onHide?: (t: Target) => void;
  onOpenSource: (s: Source) => void;
}

/**
 * One source's block on the Overview.
 *
 * The original PHP dashboard stacked Datto, ShadowProtect and Image Manager as
 * separate labelled sections, and that turned out to be the thing people liked
 * most about it: you always knew which product you were looking at without
 * reading a column, and a bad section was obvious from across the room.
 *
 * The header badge always shows the counts for the WHOLE source, not the
 * filtered subset - so filtering to "failed" still tells you it is 3 of 49
 * rather than just 3.
 */
export function SourceSection({ source, orgId, targets, counts, filtered, canHide, onHide, onOpenSource }: Props) {
  return (
    <section className="src-sec">
      <header className="src-hd">
        <button className="src-title" onClick={() => onOpenSource(source)} title="Open this source">
          {/* No mark for every vendor. A missing icon simply leaves the name,
              which is better than borrowing another product's logo. */}
          {SOURCE_ICON[source] && (
            <img className="src-icon" src={SOURCE_ICON[source]} alt="" width={30} />
          )}
          <span className="src-name">{SOURCE_LABELS[source]}</span>
        </button>
        <CountBadge counts={counts} />
      </header>

      {/* The table shapes its columns to the source: Image Manager gets the
          folder tally the old PHP site had, and Datto/ShadowProtect keep the
          Last Backup column that Image Manager has no value for. */}
      <TargetTable
        source={source}
        orgId={orgId}
        targets={targets}
        canHide={canHide}
        onHide={onHide}
        emptyMessage={
          counts.total === 0
            ? 'Nothing reported for this source yet.'
            : filtered
              ? 'Nothing in this source matches the current filter.'
              : 'No machines to show.'
        }
      />
    </section>
  );
}

/** Total, then each non-zero bucket. Zeroes are noise. */
function CountBadge({ counts }: { counts: StatusCounts }) {
  return (
    <div className="src-counts">
      <span className="cnt cnt-total">{counts.total}</span>
      {STATUSES.filter((s) => counts[s] > 0).map((s) => (
        <span key={s} className={'cnt cnt-' + s} title={s}>
          {counts[s]}
        </span>
      ))}
    </div>
  );
}
